/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { PropsWithChildren } from 'react'
import { Link, makeStyles } from '@material-ui/core'
import { TopAnnouncementBanner } from '@internal/plugin-announcements'

import MapIcon from '@material-ui/icons/MyLocation'
import LibraryBooks from '@material-ui/icons/LibraryBooks'
import SchoolIcon from '@material-ui/icons/School'

import CreateComponentIcon from '@material-ui/icons/Add'
import CatalogIcon from '@material-ui/icons/ViewList'
import ApiIcon from '@material-ui/icons/Extension'
import ComponentIcon from '@material-ui/icons/Memory'
import ResourceIcon from '@material-ui/icons/Storage'
import SystemIcon from '@material-ui/icons/Category'
import UserIcon from '@material-ui/icons/Face'
import UnprocessedIcon from '@material-ui/icons/SyncProblem'
import StatsIcon from '@material-ui/icons/Timeline'
import TasksIcon from '@material-ui/icons/Repeat'
import Dora4Icon from '@material-ui/icons/DonutSmall'
import CheckEngineIcon from '../tech-insights/CheckEngineIcon'
import VideogameAsset from '@material-ui/icons/VideogameAsset'

import LogoFull from './LogoFull'
import LogoIcon from './LogoIcon'
import { NavLink } from 'react-router-dom'
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar
} from '@backstage/plugin-user-settings'
import {
  DashboardIcon,
  GroupIcon,
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState
} from '@backstage/core-components'
// https://v4.mui.com/components/material-icons/
import MenuIcon from '@material-ui/icons/Menu'
import SearchIcon from '@material-ui/icons/Search'
// OpsGenie icon
import AccessAlarmIcon from '@material-ui/icons/AccessAlarm'
import BookmarkIcon from '@material-ui/icons/Bookmark'
import BugReportIcon from '@material-ui/icons/BugReport'
import HardwareIcon from '@material-ui/icons/Build'

import { sortByTitle } from '../../utils'
import bookmarks from '../../references/bookmarks'
import qs from 'qs'

const scorecardsQueryParams = qs.stringify({
  filters: {
    ['lifecycles']: ['production'],
    ['tags']: ['sennos']
  }
})

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24
  }
})

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles()
  const { isOpen } = useSidebarOpenState()

  return (
    <div className={classes.root}>
      <Link
        component={NavLink}
        to="/"
        underline="none"
        className={classes.link}
        aria-label="Home"
      >
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  )
}

export function Root({ children }: PropsWithChildren<{}>) {
  const inIframe = window && window.self !== window.top
  if (inIframe) {
    // If we're embedding a page (for example, to Azure DevOps),
    // render only the body content, without bells and whistles
    return <>{children}</>
  }

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarItem icon={SearchIcon} to="/search" text="Search" />
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          <SidebarItem icon={CatalogIcon} to="catalog" text="Catalog">
            <SidebarSubmenu title="Service Catalog">
              <SidebarSubmenuItem
                title="Systems"
                to="catalog?filters[kind]=system"
                icon={SystemIcon}
              />
              <SidebarSubmenuItem
                title="Components"
                to="catalog?filters[kind]=component"
                icon={ComponentIcon}
              />
              <SidebarSubmenuItem
                title="Resources"
                to="catalog?filters[kind]=resource"
                icon={ResourceIcon}
              />
              <SidebarSubmenuItem
                title="APIs"
                to="catalog?filters[kind]=api"
                icon={ApiIcon}
              />
              <SidebarDivider />
              <SidebarSubmenuItem
                title="Business Areas"
                to="catalog?filters[kind]=group&filters[type]=business-area"
                icon={DashboardIcon}
              />
              <SidebarSubmenuItem
                title="Pods"
                to="catalog?filters[kind]=group&filters[type]=pod"
                icon={GroupIcon}
              />
              <SidebarSubmenuItem
                title="Individuals"
                to="catalog?filters[kind]=user"
                icon={UserIcon}
              />
              <SidebarDivider />
              <SidebarSubmenuItem
                icon={CreateComponentIcon}
                to="create"
                title="Create Component"
              />
              <SidebarDivider />
              <SidebarSubmenuItem
                title="Unprocessed"
                to="/catalog-unprocessed-entities"
                icon={UnprocessedIcon}
              />
            </SidebarSubmenu>
          </SidebarItem>

          <SidebarItem icon={LibraryBooks} to="docs" text="Docs">
            <SidebarSubmenu title="Technical Documentation">
              <SidebarSubmenuItem
                icon={SchoolIcon}
                to="/docs/engineering-and-data-platform/component/tech-onboarding-docs"
                title="Academy"
              />
              <SidebarSubmenuItem
                icon={SchoolIcon}
                to="https://sennder.atlassian.net/wiki/x/MgBb5g"
                title="Logistics Platform Offerings"
              />
              <SidebarDivider />
              <SidebarSubmenuItem
                icon={LibraryBooks}
                to="docs"
                title="Components’ Docs"
              />
              <SidebarSubmenuItem
                icon={LibraryBooks}
                to="/docs/engineering-and-data-platform/component/developer-portal"
                title="Portal Docs"
              />
              <SidebarSubmenuItem
                icon={LibraryBooks}
                to="/docs/engineering-excellence/component/1es-documentation"
                title="1ES Docs"
              />
              <SidebarDivider />
              {sortByTitle(bookmarks).map(({ title, href }) => (
                <SidebarSubmenuItem
                  key={href}
                  icon={BookmarkIcon}
                  to={href}
                  title={title}
                />
              ))}
            </SidebarSubmenu>
          </SidebarItem>

          <SidebarItem icon={MapIcon} to="tech-radar" text="Tech Radar" />
          <SidebarItem icon={MapIcon} to="ai-tech-radar" text="AI Tech Radar" />

          <SidebarDivider />

          <SidebarItem
            icon={CheckEngineIcon}
            to={`/tech-insights-overview?${scorecardsQueryParams}`}
            text="Scorecards"
          >
            <SidebarSubmenu title="Scorecards">
              <SidebarSubmenuItem
                icon={CheckEngineIcon}
                to={`/tech-insights-overview?${scorecardsQueryParams}`}
                title="Overview"
              />
              <SidebarSubmenuItem
                icon={GroupIcon}
                to={`/tech-insights-overview/pod-performance`}
                title="Pod Performance"
              />
            </SidebarSubmenu>
          </SidebarItem>

          <SidebarItem icon={AccessAlarmIcon} to="/opsgenie" text="OpsGenie" />

          <SidebarItem icon={HardwareIcon} text="Other tools">
            <SidebarSubmenu title="Other tools">
              <SidebarSubmenuItem icon={VideogameAsset} to="/contrib" title="Contrib" />
              <SidebarSubmenuItem icon={Dora4Icon} to="/dora" title="DORA4" />
              <SidebarSubmenuItem
                icon={BugReportIcon}
                to="/testing-metrics?filters%5Bkind%5D=group"
                title="Testing Metrics"
              />
              <SidebarSubmenuItem
                icon={TasksIcon}
                to="/tasks-observability"
                title="Task Control"
              />
              <SidebarSubmenuItem icon={StatsIcon} to="stats" title="Stats" />
            </SidebarSubmenu>
          </SidebarItem>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />

        <SidebarGroup label="Settings" icon={<UserSettingsSignInAvatar />} to="/settings">
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      <TopAnnouncementBanner />
      {children}
    </SidebarPage>
  )
}
